/* iter55g47 — Admin password reset landing page.
 *
 * The email sent by /api/admin/password-reset/request links to
 * /admin/reset-password?token=... on the frontend origin. This page
 * lets the operator choose a new password, POSTs to /confirm, then
 * routes them back to /admin to sign in with the new secret.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Lock, Loader2, ChevronLeft } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PasswordStrengthMeter, { scorePassword } from '@/components/PasswordStrengthMeter';

export default function AdminResetPassword() {
  const navigate = useNavigate();
  const [params]  = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw]     = useState('');
  const [pw2, setPw2]   = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error('Missing token. Use the link from your email.'); return; }
    if (pw !== pw2) { toast.error('Passwords do not match.'); return; }
    if (scorePassword(pw).score < 2) { toast.error('Please choose a stronger password.'); return; }
    setBusy(true);
    try {
      const r = await adminApi.confirmPasswordReset(token, pw);
      toast.success('Admin password updated. Please sign in.');
      const u = r?.username ? `?user=${encodeURIComponent(r.username)}` : '';
      navigate(`/admin${u}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed. The link may have expired.');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background" data-testid="admin-reset-page">
      <div className="w-full max-w-sm rounded-md border border-border bg-panel-header/40 p-6 shadow-lg">
        <Link to="/admin" data-testid="admin-reset-back" className="mb-3 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-3 w-3" /> Back to sign in
        </Link>
        <div className="mb-3 flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-primary text-primary-foreground">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">The Loom</div>
            <div className="text-[14px] font-semibold leading-tight">Choose a new admin password</div>
          </div>
        </div>
        {!token && (
          <div className="mb-2 rounded-sm border border-amber-400/60 bg-amber-50/60 px-2 py-1.5 text-[11px] text-amber-900" data-testid="admin-reset-missing-token">
            Missing reset token. Please use the link from your email.
          </div>
        )}
        <form onSubmit={submit} className="space-y-2">
          <div>
            <Label className="text-[11.5px]">New password</Label>
            <Input type="password" required minLength={8}
                    value={pw} onChange={(e) => setPw(e.target.value)}
                    placeholder="Minimum 8 characters"
                    data-testid="admin-reset-password" />
            <PasswordStrengthMeter password={pw} />
          </div>
          <div>
            <Label className="text-[11.5px]">Confirm new password</Label>
            <Input type="password" required minLength={8}
                    value={pw2} onChange={(e) => setPw2(e.target.value)}
                    placeholder="Type it again"
                    data-testid="admin-reset-password-confirm" />
          </div>
          <Button type="submit" disabled={busy || !token} className="w-full" data-testid="admin-reset-submit">
            {busy && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            Update password
          </Button>
        </form>
      </div>
    </div>
  );
}
